"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AopsPortableText } from "@/app/components/PortableText/PortableTextComponents";
import { Typography } from "@repo/ui/aops-components/typography";
import type { FullCourseLevel, CoursePageSection } from "@repo/sanity-utils/query-types";
import { formatModalityPriceLines } from "@/lib/formatModalityPriceLines";
import { resolveModalityQuickFacts, type ModalityQuickFact } from "@/lib/learningFormatData";
import styles from "./LearningFormat.module.css";
import { SelectableTile } from "../SelectableTile";
import { Collapse } from "@repo/ui/mantine";
import { CheckIcon, CircleAlert } from "lucide-react";
import { getKeyedCopyText } from "@repo/utils/keyedCopy";
import type { CampusLocation } from "@repo/actions/AcademyApi/GetCampuses";
import { BEAST_ACADEMY_ENROLL_URL } from "@/lib/const";
import { isBeastAcademyModalitySlug } from "@/lib/beastAcademy";
export type LearningFormatProps = {
  campuses: CampusLocation[];
  /** Sanity campus slug → coursePrice overrides (empty offering campuses = all available). */
  campusCoursePrices: Readonly<Record<string, number>>;
  courseLevel: FullCourseLevel;
  coursePageSection?: NonNullable<CoursePageSection>;
  /** Dedicated Sanity section for campus location selector copy. */
  campusLocationCoursePageSection?: NonNullable<CoursePageSection>;
  // From campus querystring param or the selected-campus cookie.
  initialSelectedCampusId: string | null;
  // Modality slug from modality querystring param, matched to modalityOfferings[].modality.slug
  initialModalitySlug?: string | null;
};
export const FALLBACK_TITLE = "Learning format";
export const FALLBACK_SUBTITLE = "How does your student learn best?";
export const WHY_CHOOSE_KEY = "why-choose-header";
export const FALLBACK_WHY_CHOOSE_TITLE = "Why choose {{modality.title}}:";
export const BEST_FOR_KEY = "best-for-header";
export const FALLBACK_BEST_FOR_TITLE = "Best for students who:";
export const LEARN_MORE_KEY = "learn-more-text";
export const FALLBACK_LEARN_MORE_TITLE = "Learn more";
export const HIDE_DETAILS_KEY = "hide-details-text";
export const FALLBACK_HIDE_DETAILS_TEXT = "Hide format details";
export const LEARN_MORE_VALUE = "learn-more";
export const STEP_TAG_KEY = "step-tag";
export const FALLBACK_STEP_TAG = "STEP 1";
export type Modality = NonNullable<NonNullable<FullCourseLevel>["modalityOfferings"]>[number]["modality"];
export type ModalityOffering = NonNullable<NonNullable<FullCourseLevel>["modalityOfferings"]>[number];
export type ModalityRow = {
  modality: Modality;
  modalityOffering: ModalityOffering;
  priceLines?: ReturnType<typeof formatModalityPriceLines>;
  /** Quick facts with Billing overridden for once-billed / non-sequential courses. */
  quickFacts: ModalityQuickFact[];
  isSelectDisabled: boolean;
  isSelected: boolean;
};
export function isBeastAcademyModality(modality: Modality): boolean {
  return isBeastAcademyModalitySlug(modality.slug.current);
}

/**
 * Opens the Beast Academy enrollment link in a new tab. Called each time the
 * Beast Academy modality option is selected on the learning-format section.
 */
export function useLinkToBAO() {
  return () => {
    window.open(BEAST_ACADEMY_ENROLL_URL, "_blank", "noreferrer");
  };
}
type LearningFormatHeaderProps = {
  modality: Modality;
  modalityOffering: ModalityOffering;
  priceLines?: ReturnType<typeof formatModalityPriceLines>;
  /** Compact “Fixed” chrome while the header is stuck */
  isCompact?: boolean;
};

/**
 * Display the title of a learning format option, including the description and price.
 * When a campus is selected, `tertiary` (campus selector) renders under the title/description
 * so four-column subgrid + space-between keeps the price stack bottom-aligned with other
 * formats (WEL-1652). When `isCompact`, matches the Figma Fixed sticky state: title +
 * primary price only.
 */
export function LearningFormatHeader(t0) {
  const $ = _c(23);
  const {
    modality,
    modalityOffering,
    priceLines,
    isCompact: t1
  } = t0;
  const isCompact = t1 === undefined ? false : t1;
  const t2 = isCompact || undefined;
  let t3;
  if ($[0] !== modality.title) {
    t3 = <Typography variant="h4" as="h3" className={styles.formatTitle}>{modality.title}</Typography>;
    $[0] = modality.title;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = !isCompact;
  const t5 = modalityOffering.shortDescription?.length ? modalityOffering.shortDescription : modality.shortDescription;
  let t6;
  if ($[2] !== t5) {
    t6 = <AopsPortableText className={styles.formatShortDescription} value={t5} />;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== t4 || $[5] !== t6) {
    t7 = <Collapse expanded={t4} transitionDuration={200}>{t6}</Collapse>;
    $[4] = t4;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t3 || $[8] !== t7) {
    t8 = <div>{t3}{t7}</div>;
    $[7] = t3;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isCompact || $[11] !== priceLines) {
    t9 = priceLines?.tertiary && <Collapse expanded={!isCompact} transitionDuration={200}><div className={`${styles.priceTertiary} ${priceLines.isUnavailable ? styles.priceTertiaryUnavailable : ""}`}>{priceLines.tertiary}</div></Collapse>;
    $[10] = isCompact;
    $[11] = priceLines;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <div className={styles.formatHeaderTop}>{t8}{t9}</div>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== isCompact || $[17] !== priceLines) {
    t11 = priceLines && <div className={styles.priceStack}><Typography variant={isCompact ? "body" : "subtitle"} className={styles.pricePrimary}>{priceLines.primary}</Typography>{priceLines.secondary && <Collapse expanded={!isCompact} transitionDuration={200}><Typography variant="body" className={priceLines.isUnavailable ? styles.priceUnavailable : styles.priceSecondary}>{priceLines.isUnavailable && <CircleAlert aria-hidden="true" className={styles.priceUnavailableIcon} />}{priceLines.secondary}</Typography></Collapse>}</div>;
    $[16] = isCompact;
    $[17] = priceLines;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t2) {
    t12 = <div className={styles.formatHeader} data-compact={t2}>{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t2;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}

/**
 * Scroll distance (px) the sentinel must fully leave / re-enter the viewport
 * before toggling stuck. Helps so that the compact header doesn't bounce.
 */
const STUCK_SENTINEL_HEIGHT_PX = 100;

/**
 * Sticky header + select CTA. Compacts to the Figma Fixed state while stuck.
 * Detects when the header is at the top of the viewport via a non-sticky sentinel
 * (absolute on the tile) — instead of observing the sticky element itself, which
 * fails once the header is compact
 *
 * Accepts `disabled` from SelectableTile's child cloning without forwarding it.
 */
export function StickyFormatChrome(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel) {
        return;
      }
      const observer = new IntersectionObserver(t3 => {
        const [entry] = t3;
        if (!entry) {
          return;
        }
        setIsStuck(entry.intersectionRect.top <= 0);
      }, {
        threshold: 0,
        rootMargin: `-${STUCK_SENTINEL_HEIGHT_PX}px 0px 99999px 0px`
      });
      observer.observe(sentinel);
      return () => {
        observer.disconnect();
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={sentinelRef} className={styles.stickySentinel} style={{
      height: STUCK_SENTINEL_HEIGHT_PX
    }} aria-hidden={true} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isStuck || undefined;
  let t5;
  if ($[3] !== children || $[4] !== isStuck) {
    t5 = children(isStuck);
    $[3] = children;
    $[4] = isStuck;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <>{t3}<div className={styles.stickyHeader} data-stuck={t4}>{t5}</div></>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
type LearningFormatDescriptionProps = {
  modality: Modality;
  keyedCopy: NonNullable<CoursePageSection>["keyedCopy"];
};
export function LearningFormatWhyChoose(t0) {
  const $ = _c(12);
  const {
    modality,
    keyedCopy
  } = t0;
  if (!modality.whyChoose?.length) {
    return null;
  }
  let t1;
  if ($[0] !== keyedCopy || $[1] !== modality.title) {
    t1 = (getKeyedCopyText(keyedCopy ?? [], WHY_CHOOSE_KEY) || FALLBACK_WHY_CHOOSE_TITLE).replace("{{modality.title}}", modality.title);
    $[0] = keyedCopy;
    $[1] = modality.title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const whyChooseTitle = t1;
  let t2;
  if ($[3] !== whyChooseTitle) {
    t2 = <Typography variant="body">{whyChooseTitle}</Typography>;
    $[3] = whyChooseTitle;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== modality.whyChoose) {
    t3 = modality.whyChoose.map(_temp);
    $[5] = modality.whyChoose;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <ul>{t3}</ul>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <div className={styles.formatDescription}>{t2}{t4}</div>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp(item) {
  return <li key={item}><CheckIcon /> {item}</li>;
}
export function LearningFormatBestFor(t0) {
  const $ = _c(11);
  const {
    modality,
    keyedCopy
  } = t0;
  if (!modality.bestFor?.length) {
    return null;
  }
  let t1;
  if ($[0] !== keyedCopy) {
    t1 = getKeyedCopyText(keyedCopy ?? [], BEST_FOR_KEY) || FALLBACK_BEST_FOR_TITLE;
    $[0] = keyedCopy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const bestForTitle = t1;
  let t2;
  if ($[2] !== bestForTitle) {
    t2 = <Typography variant="body">{bestForTitle}</Typography>;
    $[2] = bestForTitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== modality.bestFor) {
    t3 = modality.bestFor.map(_temp2);
    $[4] = modality.bestFor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <ul>{t3}</ul>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className={styles.formatDescription}>{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp2(item) {
  return <li key={item}><CheckIcon /> {item}</li>;
}
export function LearningFormatQuickFact(t0) {
  const $ = _c(12);
  const {
    fact
  } = t0;
  let t1;
  if ($[0] !== fact.title) {
    t1 = <Typography variant="body">{fact.title}:</Typography>;
    $[0] = fact.title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== fact.value) {
    t2 = <Typography variant="subtitle">{fact.value}</Typography>;
    $[2] = fact.value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== fact.description) {
    t3 = fact.description && <Typography className={styles.formatFactDescription} variant="body">{fact.description}</Typography>;
    $[4] = fact.description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className={styles.formatDescription}>{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/** One subgrid row: Collapse inside Content so peer columns share height. */
export function LearningFormatDetailRow(t0) {
  const $ = _c(6);
  const {
    expanded,
    children
  } = t0;
  const t1 = expanded || undefined;
  let t2;
  if ($[0] !== children || $[1] !== expanded) {
    t2 = <Collapse expanded={expanded} transitionDuration={250} className={styles.detailsCollapse}>{children}</Collapse>;
    $[0] = children;
    $[1] = expanded;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <SelectableTile.Content className={styles.formatDetailSection} data-expanded={t1}>{t2}</SelectableTile.Content>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Select is disabled when a campus is required but missing, or when the
 * modality's course offerings list campuses and the selected one isn't among them.
 */
function isModalitySelectDisabled(modality: Modality, courseOfferings: NonNullable<FullCourseLevel>["courseOfferings"], selectedCampusId: string | null): boolean {
  const showLocationSelector = modality?.price?.displayMode === "campusLocation";
  const offeringCampuses = courseOfferings.filter(o => o.modality?._id === modality?._id).flatMap(o => o.campuses ?? []);
  const hasCampusList = offeringCampuses.length > 0;
  const selectedCampusInList = selectedCampusId != null && offeringCampuses.some(campus => campus.slug?.current === selectedCampusId);
  return showLocationSelector && !selectedCampusId || hasCampusList && !selectedCampusInList;
}
export function useModalityRows(courseLevel, selectedLearningFormat, campuses, selectedCampusId, t0, t1, campusLocationCoursePageSection) {
  const $ = _c(30);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const campusCoursePrices = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const keyedCopy = t3;
  const courseLevelSlug = courseLevel?.slug?.current ?? null;
  courseLevel?.modalityOfferings;
  courseLevel?.courseOfferings;
  courseLevel?.sequenceType;
  courseLevel?.inPersonBillingType;
  let t4;
  if ($[4] !== campusCoursePrices || $[5] !== campusLocationCoursePageSection || $[6] !== campuses || $[7] !== courseLevel?.courseOfferings || $[8] !== courseLevel?.inPersonBillingType || $[9] !== courseLevel?.modalityOfferings || $[10] !== courseLevel?.sequenceType || $[11] !== courseLevelSlug || $[12] !== keyedCopy || $[13] !== selectedCampusId || $[14] !== selectedLearningFormat) {
    bb0: {
      let t5;
      if ($[16] !== courseLevel?.courseOfferings) {
        t5 = courseLevel?.courseOfferings ?? [];
        $[16] = courseLevel?.courseOfferings;
        $[17] = t5;
      } else {
        t5 = $[17];
      }
      const courseOfferings = t5;
      const modalityOfferings = courseLevel?.modalityOfferings ?? [];
      if (!modalityOfferings?.length) {
        let t6;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = [];
          $[18] = t6;
        } else {
          t6 = $[18];
        }
        t4 = t6;
        break bb0;
      }
      let t6;
      if ($[19] !== campusCoursePrices || $[20] !== campusLocationCoursePageSection || $[21] !== campuses || $[22] !== courseLevel?.inPersonBillingType || $[23] !== courseLevel?.sequenceType || $[24] !== courseLevelSlug || $[25] !== courseOfferings || $[26] !== keyedCopy || $[27] !== selectedCampusId || $[28] !== selectedLearningFormat) {
        t6 = modalityOffering => {
          const modality = modalityOffering.modality;
          if (!modality?._id || !modality.title) {
            return null;
          }
          const isSelectDisabled = isModalitySelectDisabled(modality, courseOfferings, selectedCampusId);
          const priceLines = formatModalityPriceLines(modality, courseOfferings, courseLevel?.sequenceType, selectedCampusId, campuses, campusCoursePrices, keyedCopy, campusLocationCoursePageSection, courseLevel?.inPersonBillingType, courseLevelSlug);
          const quickFacts = resolveModalityQuickFacts(modality.quickFacts, modality, courseLevel?.sequenceType, courseLevel?.inPersonBillingType, keyedCopy);
          return {
            modality,
            modalityOffering,
            priceLines,
            quickFacts,
            isSelectDisabled: !!isSelectDisabled,
            isSelected: selectedLearningFormat === modality._id
          };
        };
        $[19] = campusCoursePrices;
        $[20] = campusLocationCoursePageSection;
        $[21] = campuses;
        $[22] = courseLevel?.inPersonBillingType;
        $[23] = courseLevel?.sequenceType;
        $[24] = courseLevelSlug;
        $[25] = courseOfferings;
        $[26] = keyedCopy;
        $[27] = selectedCampusId;
        $[28] = selectedLearningFormat;
        $[29] = t6;
      } else {
        t6 = $[29];
      }
      t4 = modalityOfferings.map(t6).filter(_temp3);
    }
    $[4] = campusCoursePrices;
    $[5] = campusLocationCoursePageSection;
    $[6] = campuses;
    $[7] = courseLevel?.courseOfferings;
    $[8] = courseLevel?.inPersonBillingType;
    $[9] = courseLevel?.modalityOfferings;
    $[10] = courseLevel?.sequenceType;
    $[11] = courseLevelSlug;
    $[12] = keyedCopy;
    $[13] = selectedCampusId;
    $[14] = selectedLearningFormat;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp3(item) {
  return item !== null;
}